// The in-memory API's structure routes (UI builder B): roles (read), seats (create, edit,
// assign, unassign, close, metric set and weights), metric definitions (create, edit, archive,
// the template catalog), target rules (create, edit, delete), observation tombstones, the
// observation and external-identity CSV imports, and scorecard publications (publish, revise,
// list). State is per fake instance and starts from the deterministic data in fake-data.ts and
// fake-entry.ts; the list routes the S1-17 block serves from that data are re-served here from
// the mutable state. Shapes are the SDK's; `fake-contract.test.ts` parses every response.

import { attainment, bandsProblem, DEFAULTS, metricHealth } from "@companyos/domain-kpi";
import { seatMetricSetVersion } from "@companyos/schema/seat-metric-set";
import type { Problem, RouteId } from "@companyos/sdk";
import { isoWeekKey } from "../focus/focus";
import { shiftWeek, weekStart } from "../focus/weeks";
import type { Ctx, Handler, Principal } from "./fake";
import {
  CATALOG,
  COMPANY_IDS,
  companyScorecard,
  type FakeCompany,
  type FakeDe,
  sharedDesName,
  uid,
} from "./fake-data";
import { type DeJobDeps, deJobHandlers, type FakeSeatMetricItem } from "./fake-de-job";
import {
  metricDefs,
  observationRow,
  parseCsv,
  people,
  periodTypeOf,
  seats as seedSeats,
  targetRules,
} from "./fake-entry";
import type { KpiStanding } from "./fake-kpi-visibility";
import { memberMetricDefs, memberSeatSets, memberSeedObservations } from "./fake-numbers";
import { seededSeatMetricSets } from "./fake-reporting";
import { cardForPeriod, periodParam } from "./fake-scorecard-grain";
import { teamKpiHandlers } from "./fake-team-kpis";
import { type TeamsDeps, teamsHandlers } from "./fake-teams";
import type { Leave } from "./fake-workforce";
import type {
  ImportBatch,
  ImportRowResult,
  MetricDef,
  MetricTemplateSet,
  Observation,
  Role,
  Scorecard,
  ScorecardMetric,
  ScorecardPublication,
  Seat,
  SeatAssignment,
  SeatMetric,
  TargetRule,
} from "./types";

/** Routes of this block behind the `kpi` add-on (merged into `FAKE_KPI_ROUTES`). */
export const STRUCTURE_KPI_ROUTES: readonly RouteId[] = [
  "metrics.create",
  "metrics.templates",
  "metrics.get",
  "metrics.update",
  "metrics.archive",
  "metrics.unarchive",
  "targets.create",
  "targets.update",
  "targets.delete",
  "observations.delete",
  "observations.import",
  "scorecards.publish",
  "scorecards.revise",
  "scorecards.publications",
  "seats.metrics",
  "seats.putMetrics",
  "seats.putMetric",
  "seats.removeMetric",
  // TEAM-KPI
  "teams.metrics",
  "teams.putMetric",
  "teams.removeMetric",
  "teams.scorecard",
  "scorecards.teams",
];

type Batch = ImportBatch & { company_id: string };

export type StructureDeps = {
  /** KPI-VISIBILITY: the caller's KPI standing (fake-kpi-visibility.ts), for the seat scorecard. */
  kpiStanding?: (p: Principal) => KpiStanding;
  /** The handlers this block overrides or delegates to (the S1-17 block). */
  base: Partial<Record<RouteId, Handler>>;
  /** The company's roles (the access fake's store, which serves `roles.*`). */
  roles: (companyId: string) => Role[];
  /** The company's people as the access fake lists them (generated plus joined). */
  peopleOf?: (p: Principal) => { id: string; kind: string }[];
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  page: <T>(items: T[], url: URL) => unknown;
  now: () => Date;
  nextSeq: () => number;
  desOf: (companyId: string) => FakeDe[];
  /** The shared observation rows (the numbers form's history and the gate read them). */
  observations: Observation[];
  batches: Batch[];
  touch: (companyId: string, week: string) => void;
  todayWeek: () => string;
  weekParam: (url: URL, name: string) => string | Response;
  seatCard: (p: Principal, de: FakeDe, week: string) => Scorecard;
  companyCard: (p: Principal, week: string) => Scorecard;
  /**
   * AGENT-REPORTING: what a seat scorecard row carries besides its numbers (the duty's status,
   * a value pending review, the last report).
   */
  metricExtras?: (
    p: Principal,
    seatId: string,
    def: MetricDef,
  ) => Pick<ScorecardMetric, "reporting" | "pending_review" | "last_report">;
  /**
   * WORKFORCE: production's `assertNoneStranded` for a DE leaving a seat (unassign, the close of
   * a seat it held): the 422 to answer, or null when every chain still ends at a person.
   */
  stranded?: (p: Principal, leave: Leave) => Response | null;
  /** WORKFORCE: a team was deleted (its agents lose the team). */
  onTeamDeleted?: (p: Principal, teamId: string) => void;
  /** CHART-AGENTS: the company's live agents (AG), for the org chart. */
  chartAgentsOf?: TeamsDeps["agentsOf"];
  /** Hands the block's seat and metric stores to the org block (fake-org.ts, M1-05/06). */
  share?: (stores: {
    seatsOf: (company: FakeCompany) => Seat[];
    metricsOf: (company: FakeCompany) => MetricDef[];
    assignmentsOf: (company: FakeCompany) => SeatAssignment[];
    /** M5-04: the teams twin's teams and members (the finance twin's tree). */
    teamsOf: TeamStores["teamsOf"];
    teamMembersOf: TeamStores["membersOf"];
    /** The company roll-up with the person-seat fallback (the operator console reads it too). */
    companyRollup: (p: Principal, week: string) => Scorecard;
    /** CHART-KB: how many of a seat's scorecard metrics have a weekly target (a completeness item). */
    measurablesOf: (company: FakeCompany, seatId: string) => number;
    /** MEMBER-ENTRY: a seat's stored metric set (fake-numbers.ts reads the holder's scope). */
    seatMetricsOf: (seatId: string) => SeatMetricItem[];
    /** MEMBER-ENTRY: seeds the Acme seat holder's numbers (idempotent). */
    seedMemberNumbers: (company: FakeCompany) => void;
    /** DE-WIZARD: DE creation's metric set on its seat (`seed_template_metrics`). */
    seedDeSeat: (company: FakeCompany, seatId: string, templateKey: string, seed: boolean) => void;
    /** AGENT-REPORTING: the seat's measured metrics (its set; a DE seat without one: the catalog). */
    measuredOf: (
      company: FakeCompany,
      seatId: string,
    ) => { metric_def_id: string; tracking_mode: SeatMetricItem["tracking_mode"] }[];
  }) => void;
  /** DE-WIZARD: whether `id` is a chat channel of the company a DE may call home. */
  channelOk?: (p: Principal, id: string) => boolean;
  /** DE-WIZARD ↔ AGENT-REPORTING: the reporting duties a DE's job sets (fake-reporting.ts). */
  agentDuty?: DeJobDeps["agentDuty"];
  /**
   * FOUNDER-OS: a company seeded by another twin (the Founder OS company's founder seat and its
   * holder, fake-portfolio.ts); null keeps the generated seats.
   */
  seedOf?: (company: FakeCompany) => { seats: Seat[]; assignments: SeatAssignment[] } | null;
  /** SHARED-DES: the org chart's `service_links` (fake-shared-des.ts). */
  serviceLinksOf?: TeamsDeps["serviceLinksOf"];
  /** SHELL-DECISIONS: the DEs a member is shown on the chart (null: every DE). */
  desShownTo?: TeamsDeps["desShownTo"];
};

type TeamStores = Parameters<NonNullable<TeamsDeps["shareTeams"]>>[0];

const EPOCH = "2026-06-01T09:00:00Z";
const CAP = DEFAULTS.attainment_cap;
const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;
const KEY = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
const DECIMAL = /^-?\d{1,12}(\.\d{1,6})?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const PERIOD_PATTERN: Record<string, RegExp> = {
  day: ISO_DATE,
  week: /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/,
  month: /^\d{4}-(0[1-9]|1[0-2])$/,
  quarter: /^\d{4}-Q[1-4]$/,
  year: /^\d{4}$/,
};
const UNITS = ["number", "currency", "percent", "yes_no", "done_not_done", "time", "rating"];
const DIRECTIONS = ["gte", "lte", "gt", "lt", "eq", "between"];
const CADENCES = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const SOURCES = ["event", "manual", "formula", "import", "task"];
const TRACKING = ["off", "context", "scored"];
const PERIOD_TYPES = ["day", "week", "month", "quarter", "year", "round"];

type SeatMetricItem = Omit<
  SeatMetric,
  | "company_id"
  | "seat_id"
  | "metric_key"
  | "metric_name"
  | "actor_kind"
  | "actor_membership_id"
  | "created_at"
  | "updated_at"
>;

const asNumber = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && DECIMAL.test(v)) return Number(v);
  return null;
};

export function structureHandlers(d: StructureDeps): Partial<Record<RouteId, Handler>> {
  const { json, problem, notFound, validation } = d;
  const forbidden = () => problem(403, "forbidden", "Forbidden");
  const stamp = () => d.now().toISOString();
  const actor = (p: Principal) => ({
    actor_kind: p.operator ? ("agency_user" as const) : ("person" as const),
    actor_membership_id: null,
  });

  // Per-company state, seeded on first use ---------------------------------------------------

  const seatStore = new Map<string, Seat[]>();
  const assignmentStore = new Map<string, SeatAssignment[]>();
  const metricStore = new Map<string, MetricDef[]>();
  const targetStore = new Map<string, TargetRule[]>();
  // AGENT-REPORTING: Riley's and the Recruiting lead's sets (the metrics their duties report).
  const seatMetricStore = new Map<string, SeatMetricItem[]>(seededSeatMetricSets());
  // MEMBER-ENTRY: Acme's Recruiting lead seat gets its member KPIs (fake-numbers.ts) and last
  // weeks' numbers when its holder first reads its numbers (`seedMemberNumbers`), so the other
  // personas' screens keep the generated data they were built on.
  let memberNumbersSeeded = false;
  function seedMemberNumbers(company: FakeCompany) {
    if (memberNumbersSeeded || company.id !== COMPANY_IDS.acme) return;
    memberNumbersSeeded = true;
    metricsOf(company).push(...memberMetricDefs(company));
    // The Recruiting lead's set also holds AGENT-REPORTING's duty metric: the member KPIs join it.
    for (const [seat, lines] of memberSeatSets()) {
      const have = seatMetricStore.get(seat) ?? [];
      const add = lines.filter((l) => !have.some((h) => h.metric_def_id === l.metric_def_id));
      seatMetricStore.set(seat, [
        ...have,
        ...add.map((l, i) => ({ ...l, position: have.length + i })),
      ]);
    }
    d.observations.push(...memberSeedObservations(d.now(), d.nextSeq));
  }
  const publications: ScorecardPublication[] = [];
  /** Observation ids each committed observation batch wrote (its reverse tombstones them). */
  const batchObservations = new Map<string, string[]>();
  const identities: { company_id: string; key: string; batch_id: string; closed: boolean }[] = [];

  // Roles live in the access fake (Setup › Roles); its seed role has the id every seeded seat
  // holds (fake-entry.ts).
  const hasRole = (company: FakeCompany, id: string) =>
    d.roles(company.id).some((r) => r.id === id);

  function seatsOf(company: FakeCompany): Seat[] {
    let list = seatStore.get(company.id);
    if (!list) {
      list = d.seedOf?.(company)?.seats ?? seedSeats(company, d.desOf(company.id));
      seatStore.set(company.id, list);
    }
    return list;
  }

  function assignmentsOf(company: FakeCompany): SeatAssignment[] {
    let list = assignmentStore.get(company.id);
    if (!list) {
      const row = (
        n: number,
        seat: string,
        member: string,
        kind: SeatAssignment["membership_kind"],
      ) => ({
        company_id: company.id,
        id: uid(0x2a00 + companyNo(company.id), n),
        seat_id: seat,
        membership_id: member,
        membership_kind: kind,
        share: "1",
        active_from: EPOCH,
        active_to: null,
        capacity: null,
        actor_kind: "system" as const,
        actor_membership_id: null,
        created_at: EPOCH,
        updated_at: EPOCH,
      });
      const out: SeatAssignment[] = [...(d.seedOf?.(company)?.assignments ?? [])];
      const staffed = new Set<string>();
      for (const de of d.desOf(company.id)) {
        if (!staffed.has(de.supervisor.seat_id)) {
          staffed.add(de.supervisor.seat_id);
          out.push(
            row(out.length + 1, de.supervisor.seat_id, de.supervisor.membership_id, "person"),
          );
        }
        out.push(row(out.length + 1, de.seat_id, de.membership_id, "digital_employee"));
      }
      list = out;
      assignmentStore.set(company.id, list);
    }
    return list;
  }

  function metricsOf(company: FakeCompany): MetricDef[] {
    let list = metricStore.get(company.id);
    if (!list) {
      list = metricDefs(company);
      metricStore.set(company.id, list);
    }
    return list;
  }

  function targetsOf(company: FakeCompany): TargetRule[] {
    let list = targetStore.get(company.id);
    if (!list) {
      list = targetRules(company);
      targetStore.set(company.id, list);
    }
    return list;
  }

  const visibleMetrics = (p: Principal) =>
    metricsOf(p.company).filter((m) => m.visibility === "client" || p.operator);
  const seatIn = (p: Principal, id: string | undefined) =>
    seatsOf(p.company).find((s) => s.id === id) ?? null;
  const activeOn = (p: Principal, seatId: string) =>
    assignmentsOf(p.company).filter((a) => a.seat_id === seatId && a.active_to === null);
  const memberKind = (p: Principal, id: string): SeatAssignment["membership_kind"] | null => {
    const des = d.desOf(p.company.id);
    if (des.some((x) => x.membership_id === id)) return "digital_employee";
    if (people(p.company, des, true).some((x) => x.id === id)) return "person";
    // People who joined through an invitation (the access fake's store).
    if (d.peopleOf?.(p).some((x) => x.id === id && x.kind === "person")) return "person";
    // The demo accounts' own person memberships (M4-03: the owner seats herself, so her crew's
    // reminders and role memory have a seat), which the generated people list leaves out.
    if (
      p.me.memberships.some(
        (m) => m.company_id === p.company.id && m.membership_id === id && m.kind === "person",
      )
    )
      return "person";
    return null;
  };

  // Seat scorecards: the seeded DE card with the seat's metric set applied ---------------------

  function blankCard(seat: Seat, week: string): Scorecard {
    const start = weekStart(week);
    const end = new Date(start.getTime() + 7 * 86_400_000 - 1000);
    return {
      subject: { kind: "seat", id: seat.id, name: seat.title },
      period: {
        type: "week",
        key: week,
        starts_at: start.toISOString(),
        ends_at: `${end.toISOString().slice(0, 19)}Z`,
      },
      computed_at: stamp(),
      kpi_score: null,
      goal_score: null,
      completeness: 0,
      band: "insufficient_data",
      metrics: [],
      contributors: [],
      cost: null,
      hours: null,
    };
  }

  /**
   * A seat's entered numbers for one metric over the card's trend window (HOME-CAD-UX: the
   * scorecard grid enters a seat's weekly value in place and reads it back): the live value per
   * week (a daily metric sums its live days), the seat's or the company's weekly target rule.
   */
  function enteredFor(
    p: Principal,
    seatId: string,
    def: MetricDef,
    week: string,
  ): Pick<ScorecardMetric, "actual" | "target" | "trend"> | null {
    if (def.source !== "manual" && def.source !== "import") return null;
    const type = periodTypeOf(def.cadence);
    if (type !== "week" && type !== "day") return null;
    const rows = d.observations.filter(
      (o) =>
        o.company_id === p.company.id &&
        o.metric_def_id === def.id &&
        o.subject_kind === "seat" &&
        o.subject_id === seatId &&
        o.period_type === type,
    );
    if (!rows.length) return null;
    const live = (periodKey: string) => {
      const cell = rows.filter((o) => o.period_key === periodKey);
      const o = cell.length ? liveOf(cell) : null;
      return o?.value === null || o?.value === undefined ? null : Number(o.value);
    };
    const valueAt = (w: string): number | null => {
      if (type === "week") return live(w);
      const start = weekStart(w);
      let sum: number | null = null;
      for (let i = 0; i < 7; i++) {
        const day = new Date(start.getTime() + i * 86_400_000).toISOString().slice(0, 10);
        const v = live(day);
        if (v !== null) sum = (sum ?? 0) + v;
      }
      return sum;
    };
    const rule = targetsOf(p.company)
      .filter(
        (t) =>
          t.metric_def_id === def.id &&
          t.period_type === "week" &&
          t.effective_to === null &&
          ((t.subject_kind === "seat" && t.subject_id === seatId) || t.subject_kind === "company"),
      )
      .sort((a) => (a.subject_kind === "seat" ? -1 : 1))[0];
    const target = rule ? Number(rule.value) : null;
    // Week keys shifted as keys (`shiftWeek`): a UTC Monday read in a local zone is Sunday.
    const weeks = Array.from({ length: 13 }, (_, i) => shiftWeek(week, i - 12));
    return {
      actual: valueAt(week),
      target,
      trend: weeks.map((w) => ({ period_key: w, actual: valueAt(w), target })),
    };
  }

  /** The card with the seat's stored metric set: membership, order, weights, tracking. */
  function withSeatMetrics(p: Principal, seatId: string, card: Scorecard): Scorecard {
    const set = seatMetricStore.get(seatId);
    if (!set) return card;
    const defs = new Map(visibleMetrics(p).map((m) => [m.id, m]));
    const metrics: ScorecardMetric[] = [];
    for (const item of [...set].sort((a, b) => a.position - b.position)) {
      const def = defs.get(item.metric_def_id);
      // An Off metric is on the seat but not on its scorecard (the API's compute skips it).
      if (!def || item.tracking_mode === "off") continue;
      const seeded = card.metrics.find((m) => m.metric_def_id === item.metric_def_id);
      const entered = enteredFor(p, seatId, def, card.period.key);
      const scored = item.tracking_mode === "scored";
      const judged =
        entered && scored && entered.actual !== null && entered.target !== null
          ? attainment(def.direction, entered.actual, entered.target, CAP, null)
          : null;
      metrics.push({
        ...(seeded ?? {
          metric_def_id: def.id,
          key: def.key,
          name: def.name,
          unit: def.unit,
          direction: def.direction,
          visibility: def.visibility,
          actual: null,
          target: null,
          target_max: null,
          attainment: null,
          expected: 5,
          present: 0,
          completeness: 0,
          state: "missing" as const,
          trend: [],
        }),
        kpi_weight: Number(item.kpi_weight),
        goal_weight: Number(item.goal_weight),
        tracking_mode: item.tracking_mode,
        ...(item.tracking_mode === "context" ? { state: "context" as const } : {}),
        // Numbers entered for this seat override the generated ones (the grid reads them back).
        ...(entered
          ? {
              ...entered,
              attainment: judged === null ? null : Math.round(judged * 1000) / 1000,
              present: entered.actual === null ? 0 : 5,
              completeness: entered.actual === null ? 0 : 1,
              state:
                item.tracking_mode === "context"
                  ? ("context" as const)
                  : entered.actual === null
                    ? ("missing" as const)
                    : ("complete" as const),
            }
          : {}),
        ...d.metricExtras?.(p, seatId, def),
      });
      // TEAM-KPI: the metric's own bands, and the line's health under them.
      const line = metrics[metrics.length - 1] as ScorecardMetric;
      line.bands = def.bands ?? null;
      line.health = metricHealth(line.attainment, def.bands);
    }
    return { ...card, metrics };
  }

  /**
   * The seat's stored set (KPI-ASSIGN's one-row writes and KPI-FIX's whole-set save share it). A
   * seat never written keeps its seeded card's lines (in card order, nothing penalty-only),
   * materialised on the first write.
   */
  function storedSet(p: Principal, seatId: string): SeatMetricItem[] {
    const have = seatMetricStore.get(seatId);
    if (have) return have;
    return (seatScorecard(p, seatId, isoWeekKey(d.now()))?.metrics ?? []).map((m, position) => ({
      metric_def_id: m.metric_def_id,
      kpi_weight: String(m.kpi_weight),
      goal_weight: String(m.goal_weight),
      tracking_mode: m.tracking_mode,
      penalty_only: false,
      position,
    }));
  }

  /**
   * `GET seats/{id}/metrics`: the stored set, every tracking mode and `penalty_only` included, or
   * for a seat never written the seeded card's lines; versioned like the API's.
   */
  function metricSetOf(p: Principal, seatId: string) {
    const defs = new Map(visibleMetrics(p).map((m) => [m.id, m]));
    const at = stamp();
    const data = [...storedSet(p, seatId)]
      .sort((a, b) => a.position - b.position)
      .flatMap((m) => {
        const def = defs.get(m.metric_def_id);
        if (!def) return [];
        return [{ ...setRow(p, seatId, m, at), metric_key: def.key, metric_name: def.name }];
      });
    return { data, version: seatMetricSetVersion(data) };
  }

  /** If-Match against the set's version, as the API checks it: 428, 422 or 409, else null. */
  function staleSet(p: Principal, seatId: string, headers: Headers): Response | null {
    const raw = headers.get("if-match");
    if (!raw) return problem(428, "precondition_required", "If-Match required");
    const expected = /^\s*(?:W\/)?"?(\d{1,9})"?\s*$/.exec(raw);
    if (!expected) return validation('If-Match: expected "{version}"');
    const current = metricSetOf(p, seatId);
    if (Number(expected[1]) !== current.version)
      return problem(409, "conflict", "Conflict", `version is ${current.version}`);
    return null;
  }
  const setRow = (
    p: Principal,
    seatId: string,
    m: SeatMetricItem,
    at = stamp(),
  ): Omit<SeatMetric, "metric_key" | "metric_name"> => ({
    ...m,
    company_id: p.company.id,
    seat_id: seatId,
    ...actor(p),
    created_at: at,
    updated_at: at,
  });

  function seatScorecard(p: Principal, seatId: string, week: string): Scorecard | null {
    const seat = seatIn(p, seatId);
    if (!seat) return null;
    const de = d.desOf(p.company.id).find((x) => x.seat_id === seatId);
    return withSeatMetrics(p, seatId, de ? d.seatCard(p, de, week) : blankCard(seat, week));
  }

  /**
   * The company roll-up (the API's `rollupSeats`, HOME-CAD-UX): every seat with KPIs, the DE
   * seats' cards (with the numbers entered here on them: the grid reads them back) and the
   * person-held open seats that carry a scored metric.
   */
  function companyRollup(p: Principal, week: string): Scorecard {
    const des = d.desOf(p.company.id);
    const held = new Set(
      assignmentsOf(p.company)
        .filter((a) => a.active_to === null && a.membership_kind === "person")
        .map((a) => a.seat_id),
    );
    const people = seatsOf(p.company).filter(
      (s) =>
        s.status !== "closed" &&
        held.has(s.id) &&
        !des.some((de) => de.seat_id === s.id) &&
        (seatMetricStore.get(s.id) ?? []).some((m) => m.tracking_mode === "scored"),
    );
    const cards = [...des.map((de) => de.seat_id), ...people.map((s) => s.id)]
      .sort()
      .flatMap((id) => {
        const card = seatScorecard(p, id, week);
        return card ? [{ card }] : [];
      });
    return companyScorecard(p.company, cards, week);
  }

  // Publications ---------------------------------------------------------------------------------

  function clientView(card: Scorecard): ScorecardPublication["client_snapshot"] {
    const clientRows = (rows: Scorecard["metrics"]) =>
      rows
        .filter((m) => m.visibility === "client")
        .map((m) => ({ ...m, visibility: "client" as const }));
    return {
      ...card,
      metrics: clientRows(card.metrics),
      ...(card.children
        ? {
            children: card.children.map((c) =>
              c.metrics ? { ...c, metrics: clientRows(c.metrics) } : c,
            ),
          }
        : {}),
      hours: card.hours
        ? { supervision_minutes: card.hours.supervision_minutes, founder_minutes: null }
        : null,
    };
  }

  function configOf(p: Principal, seatIds: string[]): ScorecardPublication["config_snapshot"] {
    return {
      seat_metrics: seatIds.flatMap((seat) =>
        (seatMetricStore.get(seat) ?? []).map((m) => ({
          seat_id: seat,
          metric_def_id: m.metric_def_id,
          kpi_weight: Number(m.kpi_weight),
          goal_weight: Number(m.goal_weight),
          tracking_mode: m.tracking_mode,
          penalty_only: m.penalty_only,
        })),
      ),
      target_rules: targetsOf(p.company).map((t) => ({
        id: t.id,
        metric_def_id: t.metric_def_id,
        subject_kind: t.subject_kind,
        subject_id: t.subject_id,
        period_type: t.period_type,
        value: Number(t.value),
        value_max: t.value_max === null ? null : Number(t.value_max),
        effective_from: t.effective_from,
        effective_to: t.effective_to,
      })),
      policy: {
        kpi_thresholds: [90, 75, 60],
        goal_thresholds: [90, 75, 60],
        attainment_cap: 1.2,
        insufficient_data_cutoff: 0.6,
      },
    };
  }

  function publicationFor(
    p: Principal,
    subject: { kind: "company" | "seat"; id: string },
    week: string,
    revision: number,
    reason: string | null,
  ): ScorecardPublication | null {
    const card =
      subject.kind === "company" ? companyRollup(p, week) : seatScorecard(p, subject.id, week);
    if (!card) return null;
    const at = stamp();
    return {
      company_id: p.company.id,
      id: uid(0x7e00, d.nextSeq()),
      subject_kind: subject.kind,
      subject_id: subject.id,
      period_type: "week",
      period_key: week,
      revision,
      snapshot: card,
      client_snapshot: clientView(card),
      config_snapshot: configOf(
        p,
        subject.kind === "seat" ? [subject.id] : seatsOf(p.company).map((s) => s.id),
      ),
      reason,
      published_at: at,
      published_by_membership_id: null,
      created_at: at,
    };
  }

  // CSV imports ----------------------------------------------------------------------------------

  type Checked = { result: ImportRowResult; write?: () => string | null };

  function checkObservations(p: Principal, text: string): Checked[] | string {
    const [header, ...data] = parseCsv(text);
    if (!header) return "the file has no header";
    const cols = header.map((h) => h.trim().toLowerCase());
    const required = ["metric_key", "subject_kind", "subject", "period_key", "value"];
    const missing = required.filter((c) => !cols.includes(c));
    if (missing.length) return `column ${missing[0]} is required`;
    if (!data.length) return "the file has no data rows";
    const seen = new Set<string>();
    return data.map((cells, i): Checked => {
      const get = (c: string) => (cells[cols.indexOf(c)] ?? "").trim();
      const errors: NonNullable<ImportRowResult["errors"]> = [];
      const err = (column: string, code: string, message: string) =>
        errors.push({ column, code, message });
      const def = visibleMetrics(p).find((m) => m.key === get("metric_key"));
      if (!def) err("metric_key", "unknown_metric", "no metric with this key in the company");
      else if (def.archived_at) err("metric_key", "metric_archived", "the metric is archived");
      else if (def.source !== "manual" && def.source !== "import")
        err("metric_key", "metric_is_derived", "the metric is computed, not entered");
      const kind = get("subject_kind");
      let subjectId: string | null = null;
      if (kind === "company") {
        if (get("subject") === "company" || get("subject") === p.company.id)
          subjectId = p.company.id;
        else err("subject", "unknown_subject", "subject is `company` for a company row");
      } else if (kind === "seat") {
        const seat = seatsOf(p.company).find(
          (s) => s.id === get("subject") || s.title === get("subject"),
        );
        if (seat) subjectId = seat.id;
        else err("subject", "unknown_seat", `no seat "${get("subject")}"`);
      } else err("subject_kind", "invalid", "subject_kind is company or seat");
      const type = def ? periodTypeOf(def.cadence) : "day";
      if (!(PERIOD_PATTERN[type] ?? ISO_DATE).test(get("period_key")))
        err("period_key", "invalid_period", `period_key must be a ${type} key`);
      const value = asNumber(get("value"));
      if (value === null) err("value", "invalid", "value must be a number");
      const cell = `${get("metric_key")}|${kind}|${subjectId}|${get("period_key")}`;
      if (seen.has(cell)) err("period_key", "duplicate_cell", "the same cell appears twice");
      seen.add(cell);
      const result: ImportRowResult = {
        row: i + 1,
        status: errors.length ? "error" : "ok",
        ...(errors.length ? { errors } : {}),
      };
      if (errors.length || !def || subjectId === null || value === null) return { result };
      return {
        result,
        write: () => {
          const n = d.nextSeq();
          const row: Observation = {
            ...observationRow(p.company.id, n, {
              metric_def_id: def.id,
              period_type: type,
              period_key: get("period_key"),
              value,
              note: get("note") || null,
              created_at: stamp(),
            }),
            subject_kind: kind as Observation["subject_kind"],
            subject_id: subjectId,
            source: "import",
            source_event_id: `import:fake:${n}`,
          };
          d.observations.push(row);
          return row.id;
        },
      };
    });
  }

  function checkIdentities(p: Principal, text: string): Checked[] | string {
    const [header, ...data] = parseCsv(text);
    if (!header) return "the file has no header";
    const cols = header.map((h) => h.trim().toLowerCase());
    const missing = ["system", "kind", "external_id"].filter((c) => !cols.includes(c));
    if (missing.length) return `column ${missing[0]} is required`;
    if (!data.length) return "the file has no data rows";
    const des = d.desOf(p.company.id);
    const folks = people(p.company, des, p.operator);
    const seen = new Set<string>();
    return data.map((cells, i): Checked => {
      const get = (c: string) => (cells[cols.indexOf(c)] ?? "").trim();
      const errors: NonNullable<ImportRowResult["errors"]> = [];
      const err = (column: string, code: string, message: string) =>
        errors.push({ column, code, message });
      if (!/^[a-z][a-z0-9_]{0,31}$/.test(get("system")))
        err("system", "invalid_system", "system is a lower-case identifier");
      const kind = get("kind");
      if (!["person", "seat", "de", "client"].includes(kind))
        err("kind", "invalid_kind", "kind is one of person, seat, de, client");
      if (!get("external_id") || get("external_id").length > 256)
        err("external_id", "invalid_external_id", "external_id is 1–256 characters");
      if (
        kind === "person" &&
        !folks.some((x) => x.email === get("membership") || x.id === get("membership"))
      )
        err("membership", "unknown_person", `no person "${get("membership")}"`);
      if (
        kind === "de" &&
        !des.some(
          (x) => x.membership_id === get("membership") || x.display_name === get("membership"),
        )
      )
        err("membership", "unknown_de", `no digital employee "${get("membership")}"`);
      if (
        kind === "seat" &&
        !seatsOf(p.company).some((s) => s.id === get("seat") || s.title === get("seat"))
      )
        err("seat", "unknown_seat", `no seat "${get("seat")}"`);
      if (kind === "client" && !get("client_ref"))
        err("client_ref", "required", "client_ref is required");
      if (get("valid_from") && !ISO_DATE.test(get("valid_from")))
        err("valid_from", "invalid_date", "valid_from must be YYYY-MM-DD");
      const key = `${get("system")}|${kind}|${get("external_id")}`;
      if (seen.has(key)) err("external_id", "duplicate_in_file", "the same mapping appears twice");
      seen.add(key);
      const live = identities.find(
        (x) => x.company_id === p.company.id && x.key === key && !x.closed,
      );
      const status: ImportRowResult["status"] = errors.length ? "error" : live ? "duplicate" : "ok";
      return {
        result: { row: i + 1, status, ...(errors.length ? { errors } : {}) },
        ...(status === "ok" ? { write: () => key } : {}),
      };
    });
  }

  function importCsv(ctx: Ctx, resource: "observations" | "external-identities"): Response {
    const p = ctx.principal;
    if (!p) return notFound();
    if (!p.manager) return forbidden();
    const form = ctx.body as { file?: string; dry_run?: string } | null;
    if (typeof form?.file !== "string") return validation("file: a CSV part is required");
    const checked =
      resource === "observations" ? checkObservations(p, form.file) : checkIdentities(p, form.file);
    if (typeof checked === "string") return validation(checked);
    const dryRun = form.dry_run !== "false";
    const rows = checked.map((c) => c.result);
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
    const batchId = uid(0x9b00, d.nextSeq());
    if (!dryRun) {
      if (rows.some((r) => r.status === "error" || r.status === "unmapped"))
        return problem(
          422,
          "validation_failed",
          "Validation failed",
          "fix every row before committing",
        );
      const written: string[] = [];
      for (const c of checked) {
        const ref = c.write?.();
        if (!ref) continue;
        if (resource === "observations") written.push(ref);
        else
          identities.push({ company_id: p.company.id, key: ref, batch_id: batchId, closed: false });
      }
      batchObservations.set(batchId, written);
      for (const id of written) {
        const o = d.observations.find((x) => x.id === id);
        if (o) d.touch(p.company.id, isoWeekKeyOf(o.period_starts_on));
      }
      d.batches.push({
        company_id: p.company.id,
        batch_id: batchId,
        resource,
        rows: rows.length,
        committed_at: stamp(),
        reversed_at: null,
      });
    }
    return json({ batch_id: batchId, resource, dry_run: dryRun, rows, counts });
  }

  /** Tombstones the live row of `target`'s cell (the previous live row applies again). */
  function tombstone(p: Principal, target: Observation): Observation {
    const n = d.nextSeq();
    const row: Observation = {
      ...target,
      id: uid(0x5f00, n),
      value: null,
      tombstone: true,
      supersedes_id: headOf(cellOf(target))?.id ?? target.id,
      source: "manual",
      source_event_id: `manual:fake:${n}`,
      note: null,
      created_at: stamp(),
    };
    d.observations.push(row);
    d.touch(p.company.id, isoWeekKeyOf(target.period_starts_on));
    return row;
  }

  const cellOf = (o: Observation) =>
    d.observations.filter(
      (x) =>
        x.company_id === o.company_id &&
        x.metric_def_id === o.metric_def_id &&
        x.subject_kind === o.subject_kind &&
        x.subject_id === o.subject_id &&
        x.period_type === o.period_type &&
        x.period_key === o.period_key,
    );

  // Handlers -------------------------------------------------------------------------------------

  const base = d.base;
  let teamStores: TeamStores | null = null;
  d.share?.({
    seatsOf,
    metricsOf,
    assignmentsOf,
    companyRollup,
    teamsOf: (company) => teamStores?.teamsOf(company) ?? [],
    teamMembersOf: (company) => teamStores?.membersOf(company) ?? [],
    seatMetricsOf: (seatId) => seatMetricStore.get(seatId) ?? [],
    seedMemberNumbers,
    measurablesOf: (company, seatId) =>
      (seatMetricStore.get(seatId) ?? []).filter((m) =>
        targetsOf(company).some(
          (t) =>
            t.metric_def_id === m.metric_def_id &&
            t.effective_to === null &&
            ((t.subject_kind === "seat" && t.subject_id === seatId) ||
              t.subject_kind === "company"),
        ),
      ).length,
    seedDeSeat: (company, seatId, templateKey, seed) => {
      // DE creation (services/api digital-employees.ts): the template's metric set, else none;
      // a seat that already holds a set keeps it.
      if (seatMetricStore.has(seatId)) return;
      if (seed && templateKey === "recruiting_de") return;
      seatMetricStore.set(seatId, []);
      void company;
    },
    measuredOf: (company, seatId) => {
      const set = seatMetricStore.get(seatId);
      if (set)
        return [...set]
          .sort((a, b) => a.position - b.position)
          .filter((m) => m.tracking_mode !== "off")
          .map((m) => ({ metric_def_id: m.metric_def_id, tracking_mode: m.tracking_mode }));
      if (!d.desOf(company.id).some((x) => x.seat_id === seatId)) return [];
      const ids = new Set(CATALOG.map((m) => m.key));
      return metricsOf(company)
        .filter((m) => ids.has(m.key))
        .map((m) => ({
          metric_def_id: m.id,
          tracking_mode: CATALOG.find((c) => c.key === m.key)?.tracking_mode ?? "scored",
        }));
    },
  });
  /** DE-WIZARD: the seat's metric set — the stored one, else the seeded DE scorecard's. */
  const seatMetricsOf = (p: Principal, seatId: string): FakeSeatMetricItem[] => {
    const stored = seatMetricStore.get(seatId);
    if (stored) return stored;
    const de = d.desOf(p.company.id).find((x) => x.seat_id === seatId && x.status !== "retired");
    if (!de) return [];
    return d.seatCard(p, de, d.todayWeek()).metrics.map((m, position) => ({
      metric_def_id: m.metric_def_id,
      kpi_weight: String(m.kpi_weight),
      goal_weight: String(m.goal_weight),
      tracking_mode: m.tracking_mode,
      penalty_only: false,
      position,
    }));
  };
  return {
    // DE-WIZARD: a DE's job set-up over these stores (fake-de-job.ts).
    ...deJobHandlers({
      json,
      problem: problem as DeJobDeps["problem"],
      notFound,
      validation,
      now: d.now,
      nextSeq: d.nextSeq,
      desOf: d.desOf,
      seatsOf,
      assignmentsOf,
      metricsOf,
      targetsOf,
      seatMetricsOf,
      setSeatMetrics: (seatId, items) => seatMetricStore.set(seatId, items),
      ...(d.channelOk ? { channelOk: d.channelOk } : {}),
      ...(d.agentDuty ? { agentDuty: d.agentDuty } : {}),
    }),
    "seats.list": ({ principal, url }) => {
      if (!principal) return notFound();
      return json(d.page(seatsOf(principal.company), url));
    },
    "seats.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      return json({ ...seat, assignments: activeOn(principal, seat.id) });
    },
    "seats.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = (body ?? {}) as Partial<{
        role_id: string;
        title: string;
        supervisor_seat_id: string | null;
        staff: boolean;
      }>;
      if (!b.role_id || !hasRole(principal.company, b.role_id))
        return validation("role_id: no such role");
      if (!b.title?.trim()) return validation("title must not be empty");
      if (b.staff && !b.supervisor_seat_id) return validation("staff: needs a supervisor");
      if (b.supervisor_seat_id) {
        const sup = seatIn(principal, b.supervisor_seat_id);
        if (!sup) return validation("supervisor_seat_id: no such seat in this company");
        if (sup.status === "closed")
          return validation("supervisor_seat_id: the supervisor seat is closed");
      }
      const at = stamp();
      const seat: Seat = {
        company_id: principal.company.id,
        id: uid(0x2b00, d.nextSeq()),
        role_id: b.role_id,
        title: b.title.trim(),
        supervisor_seat_id: b.supervisor_seat_id ?? null,
        staff: Boolean(b.staff && b.supervisor_seat_id),
        kind: "standard",
        status: "open",
        origin: "manual",
        origin_ref: null,
        settings: {},
        de_fill: null,
        version: 1,
        ...actor(principal),
        created_at: at,
        updated_at: at,
      };
      seatsOf(principal.company).push(seat);
      return json(seat, 201);
    },
    "seats.update": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      const b = (body ?? {}) as Partial<
        Pick<Seat, "role_id" | "title" | "supervisor_seat_id" | "staff" | "status" | "settings">
      >;
      // CHART-STAFF (as the API): staff needs a supervisor; clearing it clears staff.
      const nextSup =
        b.supervisor_seat_id !== undefined ? b.supervisor_seat_id : seat.supervisor_seat_id;
      if (b.staff === true && !nextSup) return validation("staff: needs a supervisor");
      if (b.settings !== undefined && (typeof b.settings !== "object" || b.settings === null))
        return validation("settings: an object");
      if (b.title !== undefined && !b.title.trim()) return validation("title must not be empty");
      if (b.role_id !== undefined && !hasRole(principal.company, b.role_id))
        return validation("role_id: no such role");
      if (b.status !== undefined && !["open", "filled", "paused"].includes(b.status))
        return validation("status: open, filled or paused");
      if (b.supervisor_seat_id) {
        if (b.supervisor_seat_id === seat.id)
          return validation("supervisor_seat_id: a seat cannot report to itself");
        if (!seatIn(principal, b.supervisor_seat_id))
          return validation("supervisor_seat_id: no such seat in this company");
      }
      Object.assign(seat, {
        ...(b.title !== undefined ? { title: b.title.trim() } : {}),
        ...(b.role_id !== undefined ? { role_id: b.role_id } : {}),
        ...(b.status !== undefined ? { status: b.status } : {}),
        ...(b.supervisor_seat_id !== undefined ? { supervisor_seat_id: b.supervisor_seat_id } : {}),
        ...(b.staff !== undefined || !nextSup ? { staff: Boolean(b.staff && nextSup) } : {}),
        // M3-08: the thread gate's `require_approval` lives in the seat's settings.
        ...(b.settings !== undefined ? { settings: b.settings } : {}),
        version: seat.version + 1,
        updated_at: stamp(),
      });
      return json(seat);
    },
    "seats.assign": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      const b = (body ?? {}) as Partial<{ membership_id: string }>;
      const kind = b.membership_id ? memberKind(principal, b.membership_id) : null;
      if (!b.membership_id || !kind) return validation("membership_id: no such member");
      const active = activeOn(principal, seat.id);
      if (active.some((a) => a.membership_kind === kind))
        return problem(
          409,
          "seat_occupied",
          "Seat occupied",
          `the seat already has an active ${kind === "person" ? "person" : "DE"}`,
        );
      if (kind === "digital_employee") {
        const sup = seat.supervisor_seat_id;
        if (!sup)
          return problem(
            422,
            "supervisor_required",
            "Supervisor required",
            "the seat has no supervisor seat",
          );
        // The reporting rule (2026-10-26): up through DE-held seats to a person. A DE holds its
        // seat while not retired (a paused DE still counts), as the API.
        const liveDe = new Set(
          d
            .desOf(principal.company.id)
            .filter((x) => x.status !== "retired")
            .map((x) => x.membership_id),
        );
        const seen = new Set<string>();
        let at: string | null = sup;
        let ends = false;
        while (at !== null && !seen.has(at)) {
          seen.add(at);
          const holders = activeOn(principal, at);
          if (holders.some((a) => a.membership_kind === "person")) {
            ends = true;
            break;
          }
          if (
            !holders.some(
              (a) => a.membership_kind === "digital_employee" && liveDe.has(a.membership_id),
            )
          )
            break;
          at = seatIn(principal, at)?.supervisor_seat_id ?? null;
        }
        if (!ends)
          return problem(
            422,
            "supervisor_required",
            "Supervisor required",
            "the supervisor seat's reporting chain does not end at a person",
          );
      }
      const at = stamp();
      const row: SeatAssignment = {
        company_id: principal.company.id,
        id: uid(0x2c00, d.nextSeq()),
        seat_id: seat.id,
        membership_id: b.membership_id,
        membership_kind: kind,
        share: "1",
        active_from: at,
        active_to: null,
        capacity: null,
        ...actor(principal),
        created_at: at,
        updated_at: at,
      };
      assignmentsOf(principal.company).push(row);
      Object.assign(seat, { status: "filled", updated_at: at });
      return json(row, 201);
    },
    "seats.unassign": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      const b = (body ?? {}) as Partial<{ membership_id: string }>;
      const row = activeOn(principal, seat.id).find((a) => a.membership_id === b.membership_id);
      if (!row) return notFound();
      // WORKFORCE: a DE leaving re-checks every chain below and its agents (a person: not).
      if (row.membership_kind === "digital_employee") {
        const refused = d.stranded?.(principal, {
          seatIds: [seat.id],
          membershipIds: [row.membership_id],
          drop: [{ seat_id: seat.id, membership_id: row.membership_id }],
        });
        if (refused) return refused;
      }
      const at = stamp();
      Object.assign(row, { active_to: at, updated_at: at });
      if (!activeOn(principal, seat.id).length)
        Object.assign(seat, { status: "open", updated_at: at });
      return json(row);
    },
    "seats.close": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      // WORKFORCE: closing a seat a DE held re-checks every chain below and its agents.
      const deHolders = activeOn(principal, seat.id)
        .filter((a) => a.membership_kind === "digital_employee")
        .map((a) => a.membership_id);
      if (deHolders.length) {
        const refused = d.stranded?.(principal, {
          seatIds: [seat.id],
          membershipIds: deHolders,
          closeSeats: [seat.id],
        });
        if (refused) return refused;
      }
      const at = stamp();
      for (const a of activeOn(principal, seat.id))
        Object.assign(a, { active_to: at, updated_at: at });
      Object.assign(seat, { status: "closed", version: seat.version + 1, updated_at: at });
      return json(seat);
    },
    "seats.metrics": ({ principal, params }) => {
      if (!principal) return notFound();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      const set = metricSetOf(principal, seat.id);
      return json(set);
    },
    "seats.putMetrics": ({ principal, params, body, headers }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (!headers.get("if-match"))
        return problem(428, "precondition_required", "If-Match required");
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      const stale = staleSet(principal, seat.id, headers);
      if (stale) return stale;
      const items = ((body ?? {}) as { items?: unknown }).items;
      if (!Array.isArray(items)) return validation("items: an array is required");
      const defs = new Map(visibleMetrics(principal).map((m) => [m.id, m]));
      const seen = new Set<string>();
      const out: SeatMetricItem[] = [];
      for (const [n, raw] of items.entries()) {
        const i = raw as Partial<SeatMetricItem & { kpi_weight: unknown; goal_weight: unknown }>;
        const def = i.metric_def_id ? defs.get(i.metric_def_id) : undefined;
        if (!def) return validation(`items.${n}.metric_def_id: no such metric`);
        if (def.archived_at) return validation(`items.${n}.metric_def_id: the metric is archived`);
        if (seen.has(def.id)) return validation("items: a metric may appear once");
        seen.add(def.id);
        const kpi = asNumber(i.kpi_weight);
        const goal = asNumber(i.goal_weight);
        if (kpi === null || kpi < 0 || kpi > 999.999)
          return validation(`items.${n}.kpi_weight: 0 to 999.999`);
        if (goal === null || goal < 0 || goal > 999.999)
          return validation(`items.${n}.goal_weight: 0 to 999.999`);
        if (!TRACKING.includes(String(i.tracking_mode)))
          return validation(`items.${n}.tracking_mode`);
        out.push({
          metric_def_id: def.id,
          kpi_weight: String(kpi),
          goal_weight: String(goal),
          tracking_mode: i.tracking_mode as SeatMetricItem["tracking_mode"],
          penalty_only: i.penalty_only === true,
          position: Number.isInteger(i.position) ? (i.position as number) : n,
        });
      }
      seatMetricStore.set(seat.id, out);
      return json(metricSetOf(principal, seat.id));
    },
    // KPI-ASSIGN: one row written or taken off alone, under the set's If-Match like the API.
    "seats.putMetric": ({ principal, params, body, headers }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      const def = visibleMetrics(principal).find((m) => m.id === params.metric_id);
      if (!def) return notFound();
      if (def.archived_at) return validation("metric_id: the metric is archived");
      const stale = staleSet(principal, seat.id, headers);
      if (stale) return stale;
      const b = (body ?? {}) as Partial<Record<keyof SeatMetricItem, unknown>>;
      const kpi = asNumber(b.kpi_weight);
      const goal = asNumber(b.goal_weight);
      if (kpi === null || kpi < 0 || kpi > 999.999) return validation("kpi_weight: 0 to 999.999");
      if (goal === null || goal < 0 || goal > 999.999)
        return validation("goal_weight: 0 to 999.999");
      if (!TRACKING.includes(String(b.tracking_mode))) return validation("tracking_mode");
      if (typeof b.penalty_only !== "boolean") return validation("penalty_only: a boolean");
      const set = [...storedSet(principal, seat.id)];
      const i = set.findIndex((m) => m.metric_def_id === def.id);
      const position =
        typeof b.position === "number"
          ? b.position
          : (set[i]?.position ?? set.reduce((max, m) => Math.max(max, m.position + 1), 0));
      const row: SeatMetricItem = {
        metric_def_id: def.id,
        kpi_weight: String(kpi),
        goal_weight: String(goal),
        tracking_mode: b.tracking_mode as SeatMetricItem["tracking_mode"],
        penalty_only: b.penalty_only,
        position,
      };
      if (i >= 0) set[i] = row;
      else set.push(row);
      seatMetricStore.set(seat.id, set);
      return json(metricSetOf(principal, seat.id));
    },
    "seats.removeMetric": ({ principal, params, headers }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const seat = seatIn(principal, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat closed");
      // Idempotent like the API: only an agency row a client may not see is a 404.
      const hidden = metricsOf(principal.company).some(
        (m) => m.id === params.metric_id && m.visibility === "agency" && !principal.operator,
      );
      if (hidden) return notFound();
      const stale = staleSet(principal, seat.id, headers);
      if (stale) return stale;
      seatMetricStore.set(
        seat.id,
        storedSet(principal, seat.id).filter((m) => m.metric_def_id !== params.metric_id),
      );
      return json(metricSetOf(principal, seat.id));
    },
    // KPIS-MODE: a week, a month or a quarter (fake-scorecard-grain composes the longer grains).
    "seats.scorecard": ({ principal, params, url }) => {
      if (!principal) return notFound();
      // KPI-VISIBILITY (the API's `seatReader`): managers and client viewers read every seat;
      // a member its own seats, the branch below them and what its grants open (403 otherwise).
      if (
        !principal.manager &&
        !principal.roles.includes("client_viewer") &&
        d.kpiStanding &&
        !d.kpiStanding(principal).sees("kpi", params.id ?? "")
      )
        return problem(403, "forbidden", "Forbidden", "this seat's KPIs are not shared with you");
      const period = periodParam(url, d.todayWeek);
      if ("error" in period) return validation(period.error);
      const card = cardForPeriod(period, (w) => seatScorecard(principal, params.id ?? "", w));
      return card ? json(card) : notFound();
    },
    "scorecards.company": ({ principal, url }) => {
      if (!principal) return notFound();
      const period = periodParam(url, d.todayWeek);
      if ("error" in period) return validation(period.error);
      return json(cardForPeriod(period, (w) => companyRollup(principal, w)));
    },
    "digitalEmployees.scorecard": ({ principal, params, url }) => {
      if (!principal) return notFound();
      const de = d.desOf(principal.company.id).find((x) => x.membership_id === params.id);
      if (!de) return notFound();
      const week = d.weekParam(url, "period");
      if (typeof week !== "string") return week;
      return json(withSeatMetrics(principal, de.seat_id, d.seatCard(principal, de, week)));
    },

    "metrics.list": ({ principal, url }) => {
      if (!principal) return notFound();
      return json(d.page(visibleMetrics(principal), url));
    },
    "metrics.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const m = visibleMetrics(principal).find((x) => x.id === params.id);
      return m ? json(m) : notFound();
    },
    "metrics.templates": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      // The DE catalog, whatever the company defines (HTVA's own company has none of it).
      const catalog = metricDefs({ ...principal.company, id: COMPANY_IDS.acme });
      const defs = new Map(catalog.map((m) => [m.key, m]));
      const set: MetricTemplateSet = {
        template_key: "recruiting_de",
        metrics: CATALOG.flatMap((spec) => {
          const def = defs.get(spec.key);
          if (!def) return [];
          return [
            {
              key: spec.key,
              name: spec.name,
              unit: spec.unit,
              direction: spec.direction,
              aggregation: def.aggregation,
              cadence: def.cadence,
              source: def.source,
              event_query: def.event_query,
              formula: def.formula,
              visibility: spec.visibility,
              kpi_weight: spec.kpi_weight,
              goal_weight: spec.goal_weight,
              tracking_mode: spec.tracking_mode,
              default_weekly_target: spec.target,
            },
          ];
        }),
      };
      return json({ data: set.metrics.length ? [set] : [] });
    },
    "metrics.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = (body ?? {}) as Partial<MetricDef>;
      const bad = checkMetric(b, true);
      if (bad) return validation(bad);
      if (b.visibility === "agency" && !principal.operator)
        return problem(403, "forbidden", "Forbidden", "only the agency defines agency metrics");
      const list = metricsOf(principal.company);
      if (list.some((m) => m.key === b.key))
        return problem(409, "conflict", "Conflict", `metric key ${b.key} exists`);
      const at = stamp();
      const m: MetricDef = {
        company_id: principal.company.id,
        id: uid(0x3e00, d.nextSeq()),
        key: b.key as string,
        name: (b.name as string).trim(),
        description: b.description ?? null,
        unit: b.unit as MetricDef["unit"],
        direction: b.direction as MetricDef["direction"],
        aggregation: b.aggregation as MetricDef["aggregation"],
        normalization: b.normalization ?? null,
        cadence: b.cadence ?? null,
        source: b.source as MetricDef["source"],
        event_query: b.event_query ?? null,
        formula: b.formula ?? null,
        task_query: b.task_query ?? null,
        visibility: b.visibility ?? "client",
        gap_policy: b.gap_policy ?? "excuse",
        needs_review: b.needs_review === true,
        archived_at: null,
        bands: b.bands ?? null,
        gate: b.gate ?? null,
        ...actor(principal),
        created_at: at,
        updated_at: at,
      };
      list.push(m);
      return json(m, 201);
    },
    "metrics.update": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const m = visibleMetrics(principal).find((x) => x.id === params.id);
      if (!m) return notFound();
      const b = (body ?? {}) as Partial<MetricDef>;
      if ("key" in b) return validation("key is immutable");
      if ("needs_review" in b && typeof b.needs_review !== "boolean")
        return validation("needs_review: a boolean");
      const merged = { ...m, ...b };
      const bad = checkMetric(merged, false);
      if (bad) return validation(bad);
      if (merged.visibility === "agency" && !principal.operator)
        return problem(403, "forbidden", "Forbidden", "only the agency defines agency metrics");
      Object.assign(m, b, { name: merged.name.trim(), updated_at: stamp() });
      return json(m);
    },
    "metrics.archive": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const m = visibleMetrics(principal).find((x) => x.id === params.id);
      if (!m) return notFound();
      if (!m.archived_at) Object.assign(m, { archived_at: stamp(), updated_at: stamp() });
      return json(m);
    },
    // ACTIONS: an archived metric live again (409 when it is not archived).
    "metrics.unarchive": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const m = visibleMetrics(principal).find((x) => x.id === params.id);
      if (!m) return notFound();
      if (!m.archived_at) return problem(409, "conflict", "Conflict", "the metric is not archived");
      Object.assign(m, { archived_at: null, updated_at: stamp() });
      return json(m);
    },

    "targets.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const q = (k: string) => url.searchParams.get(k);
      const visible = new Set(visibleMetrics(principal).map((m) => m.id));
      const rows = targetsOf(principal.company).filter(
        (t) =>
          visible.has(t.metric_def_id) &&
          (!q("metric") || t.metric_def_id === q("metric")) &&
          (!q("subject_kind") || t.subject_kind === q("subject_kind")) &&
          (!q("subject_id") || t.subject_id === q("subject_id")),
      );
      return json(d.page(rows, url));
    },
    "targets.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = (body ?? {}) as Partial<Record<keyof TargetRule, unknown>>;
      const m = visibleMetrics(principal).find((x) => x.id === b.metric_def_id);
      if (!m) return validation("metric_def_id: no such metric");
      if (m.archived_at) return validation("metric_def_id: the metric is archived");
      if (b.subject_kind === "company") {
        if (b.subject_id !== principal.company.id) return validation("subject_id: the company");
      } else if (b.subject_kind === "seat") {
        if (!seatIn(principal, String(b.subject_id))) return validation("subject_id: no such seat");
      } else return validation("subject_kind: company or seat");
      if (!PERIOD_TYPES.includes(String(b.period_type))) return validation("period_type");
      const bad = checkWindow(b);
      if (bad) return validation(bad);
      const at = stamp();
      const t: TargetRule = {
        company_id: principal.company.id,
        id: uid(0x7b00, d.nextSeq()),
        metric_def_id: m.id,
        subject_kind: b.subject_kind,
        subject_id: String(b.subject_id),
        period_type: b.period_type as TargetRule["period_type"],
        value: String(asNumber(b.value)),
        value_max:
          b.value_max === undefined || b.value_max === null ? null : String(asNumber(b.value_max)),
        effective_from: String(b.effective_from),
        effective_to: (b.effective_to as string | null | undefined) ?? null,
        ...actor(principal),
        created_at: at,
        updated_at: at,
      };
      targetsOf(principal.company).push(t);
      return json(t, 201);
    },
    "targets.update": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const t = targetsOf(principal.company).find((x) => x.id === params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as Partial<Record<keyof TargetRule, unknown>>;
      const merged = { ...t, ...b };
      const bad = checkWindow(merged);
      if (bad) return validation(bad);
      Object.assign(t, {
        ...(b.value !== undefined ? { value: String(asNumber(b.value)) } : {}),
        ...(b.value_max !== undefined
          ? { value_max: b.value_max === null ? null : String(asNumber(b.value_max)) }
          : {}),
        ...(b.effective_from !== undefined ? { effective_from: b.effective_from } : {}),
        ...(b.effective_to !== undefined ? { effective_to: b.effective_to } : {}),
        updated_at: stamp(),
      });
      return json(t);
    },
    "targets.delete": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const list = targetsOf(principal.company);
      const i = list.findIndex((x) => x.id === params.id);
      if (i < 0) return notFound();
      list.splice(i, 1);
      return new Response(null, { status: 204 });
    },

    "observations.delete": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const target = d.observations.find(
        (o) => o.company_id === principal.company.id && o.id === params.id,
      );
      if (!target) return notFound();
      const def = metricsOf(principal.company).find((m) => m.id === target.metric_def_id);
      if (def && def.source !== "manual" && def.source !== "import")
        return problem(422, "metric_is_derived", "Derived metric", "the metric is computed");
      if (liveOf(cellOf(target))?.id !== target.id)
        return problem(409, "conflict", "Conflict", "the value is no longer live");
      return json(tombstone(principal, target));
    },
    "observations.import": (ctx) => importCsv(ctx, "observations"),
    "externalIdentities.import": (ctx) => importCsv(ctx, "external-identities"),
    "imports.reverse": (ctx) => {
      const p = ctx.principal;
      const batch = p
        ? d.batches.find((b) => b.company_id === p.company.id && b.batch_id === ctx.params.batch)
        : null;
      if (
        !p ||
        !batch ||
        (batch.resource !== "observations" && batch.resource !== "external-identities")
      )
        return base["imports.reverse"]?.(ctx) ?? notFound();
      if (!p.operator) return forbidden();
      if (batch.reversed_at) return problem(409, "conflict", "Conflict", "already reversed");
      let tombstoned = 0;
      let closed = 0;
      for (const id of batchObservations.get(batch.batch_id) ?? []) {
        const o = d.observations.find((x) => x.id === id);
        if (o && liveOf(cellOf(o))?.id === o.id) {
          tombstone(p, o);
          tombstoned++;
        }
      }
      for (const x of identities)
        if (x.batch_id === batch.batch_id && !x.closed) {
          x.closed = true;
          closed++;
        }
      batch.reversed_at = stamp();
      return json({
        batch_id: batch.batch_id,
        voided_time_entries: 0,
        tombstoned_observations: tombstoned,
        reversed_cost_events: 0,
        closed_external_identities: closed,
      });
    },

    "scorecards.publications": ({ principal, url }) => {
      if (!principal) return notFound();
      const q = (k: string) => url.searchParams.get(k);
      const rows = publications
        .filter(
          (x) =>
            x.company_id === principal.company.id &&
            (!q("subject_kind") || x.subject_kind === q("subject_kind")) &&
            (!q("subject_id") || x.subject_id === q("subject_id")),
        )
        .map((x) => (principal.manager ? x : { ...x, snapshot: x.client_snapshot }))
        .sort((a, b) => b.published_at.localeCompare(a.published_at) || b.revision - a.revision);
      return json(d.page(rows, url));
    },
    "scorecards.publish": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = (body ?? {}) as Partial<{
        subject_kind: string;
        subject_id: string;
        period_type: string;
        period_key: string;
      }>;
      if (b.period_type !== "week" || !PERIOD_PATTERN.week?.test(b.period_key ?? ""))
        return validation("period_type=week with a week key (the fake publishes weeks only)");
      if (b.subject_kind === "company" && b.subject_id !== principal.company.id)
        return validation("subject_id: the company");
      if (b.subject_kind !== "company" && b.subject_kind !== "seat")
        return validation("subject_kind: company or seat");
      if (
        publications.some(
          (x) =>
            x.company_id === principal.company.id &&
            x.subject_kind === b.subject_kind &&
            x.subject_id === b.subject_id &&
            x.period_key === b.period_key,
        )
      )
        return problem(
          409,
          "already_published",
          "Already published",
          "revise the publication instead",
        );
      const pub = publicationFor(
        principal,
        { kind: b.subject_kind, id: b.subject_id ?? "" },
        b.period_key as string,
        1,
        null,
      );
      if (!pub) return validation("subject_id: no such seat");
      publications.push(pub);
      return json(pub, 201);
    },
    "scorecards.revise": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const from = publications.find(
        (x) => x.company_id === principal.company.id && x.id === params.id,
      );
      if (!from) return notFound();
      const reason = String(((body ?? {}) as { reason?: unknown }).reason ?? "").trim();
      if (reason.length < 10) return validation("reason: at least 10 characters");
      const latest = Math.max(
        ...publications
          .filter(
            (x) =>
              x.company_id === from.company_id &&
              x.subject_kind === from.subject_kind &&
              x.subject_id === from.subject_id &&
              x.period_key === from.period_key,
          )
          .map((x) => x.revision),
      );
      // HELPER-KPIS: a helper's scorecard is never published either.
      if (from.subject_kind === "team" || from.subject_kind === "agent") return notFound();
      const pub = publicationFor(
        principal,
        { kind: from.subject_kind, id: from.subject_id },
        from.period_key,
        latest + 1,
        reason,
      );
      if (!pub) return notFound();
      publications.push(pub);
      return json(pub, 201);
    },
    // M1-08: teams and the org chart (fake-teams.ts), over these seats and assignments (one store).
    ...teamsHandlers({
      json,
      problem,
      notFound,
      validation,
      page: d.page,
      now: d.now,
      nextSeq: d.nextSeq,
      seatsOf,
      assignmentsOf,
      ...(d.onTeamDeleted ? { onDeleted: d.onTeamDeleted } : {}),
      ...(d.chartAgentsOf ? { agentsOf: d.chartAgentsOf } : {}),
      ...(d.serviceLinksOf ? { serviceLinksOf: d.serviceLinksOf } : {}),
      ...(d.desShownTo ? { desShownTo: d.desShownTo } : {}),
      shareTeams: (stores) => {
        teamStores = stores;
      },
      nameOf: (company, id) =>
        d.desOf(company.id).find((x) => x.membership_id === id)?.display_name ??
        people(company, d.desOf(company.id), true).find((x) => x.id === id)?.display_name ??
        sharedDesName(id) ??
        "Member",
    }),
    // TEAM-KPI: team KPIs (fake-team-kpis.ts) over these metrics, targets, seats and teams.
    ...teamKpiHandlers({
      json,
      problem,
      notFound,
      validation,
      now: d.now,
      nextSeq: d.nextSeq,
      todayWeek: d.todayWeek,
      weekParam: d.weekParam,
      metricsOf,
      targetsOf,
      seatsOf,
      assignmentsOf,
      teamsOf: (company) => teamStores?.teamsOf(company) ?? [],
      membersOf: (company) => teamStores?.membersOf(company) ?? [],
      observations: d.observations,
      liveOf,
      seatCard: seatScorecard,
      ...(d.kpiStanding ? { kpiStanding: d.kpiStanding } : {}),
      newId: (n) => uid(0x7f61, n),
    }),
  };
}

/** A metric definition's own rules (the contract's and the route's), or null when valid. */
function checkMetric(m: Partial<MetricDef>, create: boolean): string | null {
  // TEAM-KPI: the metric's own warning bands (domain-kpi `bandsProblem`, the contract's range).
  if (m.bands) {
    const why = bandsProblem(m.bands);
    if (why) return `bands: ${why}`;
  }
  if (create && (!m.key || !KEY.test(m.key) || m.key.length > 64))
    return "key: dotted lower-case key, e.g. de.outcomes";
  if (!m.name?.trim()) return "name must not be empty";
  if (!UNITS.includes(String(m.unit))) return "unit";
  if (!DIRECTIONS.includes(String(m.direction))) return "direction";
  if (m.aggregation !== "cumulative" && m.aggregation !== "average") return "aggregation";
  if (m.cadence != null && !CADENCES.includes(m.cadence)) return "cadence";
  if (!SOURCES.includes(String(m.source))) return "source";
  if ((m.source === "event") !== (m.event_query != null))
    return "event_query: required iff source = event";
  if ((m.source === "formula") !== (m.formula != null && m.formula !== ""))
    return "formula: required iff source = formula";
  // T3-TASK-ROLLUPS: a task metric names its board and measure; an average averages.
  if ((m.source === "task") !== (m.task_query != null))
    return "task_query: required iff source = task";
  if (m.task_query && m.aggregation !== (m.task_query.measure === "avg" ? "average" : "cumulative"))
    return "aggregation: follows the task metric's measure";
  return null;
}

function checkWindow(t: Partial<Record<string, unknown>>): string | null {
  if (asNumber(t.value) === null) return "value must be a number";
  if (t.value_max !== undefined && t.value_max !== null && asNumber(t.value_max) === null)
    return "value_max must be a number";
  if (!ISO_DATE.test(String(t.effective_from))) return "effective_from must be YYYY-MM-DD";
  if (t.effective_to !== undefined && t.effective_to !== null) {
    if (!ISO_DATE.test(String(t.effective_to))) return "effective_to must be YYYY-MM-DD";
    if (String(t.effective_to) < String(t.effective_from))
      return "effective_to is before effective_from";
  }
  return null;
}

/** Newest first: the cell's rows by `created_at`. */
const chainOf = (rows: Observation[]) =>
  [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));

const headOf = (rows: Observation[]) => chainOf(rows)[0] ?? null;

/** domain-kpi `liveOf`: each tombstone cancels the next older value row. */
export function liveOf(rows: Observation[]): Observation | null {
  let pending = 0;
  for (const r of chainOf(rows)) {
    if (r.tombstone) pending += 1;
    else if (pending > 0) pending -= 1;
    else return r;
  }
  return null;
}

const isoWeekKeyOf = (day: string) => isoWeekKey(new Date(`${day}T12:00:00Z`));
